import { CircleAlert, CircleCheck, RotateCcw, X } from "lucide-react";
import { Stax } from "@/components/stax/Stax";
import type { ConversationMessage } from "../api/contract";
import type { UndoState } from "../hooks/useUndo";
import { Spinner } from "./Spinner";

interface PeekProps {
  message: ConversationMessage;
  onOpen: () => void;
  onDismiss: () => void;
}

/** A reply that arrived while the panel was hidden. */
export function Peek({ message, onOpen, onDismiss }: PeekProps) {
  return (
    <aside className="bw-peek" aria-label="New message from Stax" role="status">
      <div className="bw-peek-head">
        <Stax variant="mini" size={28} mood="idle" />
        <strong>Stax replied</strong>
      </div>
      <p>{message.text}</p>
      <div className="bw-peek-actions">
        <button type="button" className="bw-btn bw-toast-quiet" onClick={onDismiss}>Keep browsing</button>
        <button type="button" className="bw-btn bw-toast-action" onClick={onOpen}>Open chat</button>
      </div>
    </aside>
  );
}

interface UndoToastProps {
  state: UndoState;
  onUndo: () => void;
  onDismiss: () => void;
  /** How long Undo is offered; matches `useUndo`. */
  offerMs?: number;
}

/** What just happened, in one line, for the toast that offers to undo it. */
export function undoMessage(state: Exclude<UndoState, { phase: "idle" }>): string {
  switch (state.phase) {
    case "offered": {
      if (state.kind === "brand.set") return "Brand guidelines updated.";
      if (state.kind === "site.set") return "Site details updated.";
      if (state.kind === "draft.restore") return "Earlier version restored.";
      if (state.kind === "content.edit" || state.kind === "content.patch-instance") return "Text updated in your draft.";
      if (state.kind === "content.add") return "Content added to your draft.";
      if (state.kind === "content.delete") return state.subject ? `Deleted “${state.subject.label}”.` : "Deleted from your Content.";
      if (state.kind === "media.add") return "Photo added to your draft.";
      if (state.kind === "media.replace" || state.kind === "media.bind") return "Photo replaced in your draft.";
      if (state.kind === "media.crop") return "Photo cropped in your draft.";
      if (state.kind === "section.hide") return "Section updated in your draft.";
      if (state.kind === "change.undo") return "Change undone.";
      return "Design updated in your draft.";
    }
    case "undoing":
      return "Undoing…";
    case "undone":
      return "Change undone.";
    case "failed":
      return "Couldn’t undo. Ask Stax to change it back.";
  }
}

/** How long an undo stays on offer, drawn as a line that runs out. */
export function ToastTimer({ ms }: { ms: number }) {
  return <span className="bw-toast-timer" style={{ animationDuration: `${ms}ms` }} aria-hidden="true" />;
}

/** The toast's leading mark: saved, undone or failed. */
export function ToastIcon({ tone }: { tone: "done" | "undone" | "failed" }) {
  const Icon = tone === "failed" ? CircleAlert : tone === "undone" ? RotateCcw : CircleCheck;
  return <Icon className="bw-toast-icon" data-tone={tone} size={20} aria-hidden="true" />;
}

/** Closes a toast without undoing anything. */
export function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button type="button" className="bw-btn bw-btn--icon" aria-label="Dismiss" onClick={onDismiss}>
      <X size={18} aria-hidden="true" />
    </button>
  );
}

/** Recent content changes can be taken back from this toast. */
export function UndoToast({ state, onUndo, onDismiss, offerMs = 15_000 }: UndoToastProps) {
  if (state.phase === "idle") return null;
  return (
    <div className="bw-toast" role="status">
      {state.phase === "undoing" ? <Spinner /> : <ToastIcon tone={state.phase === "failed" ? "failed" : state.phase === "undone" ? "undone" : "done"} />}
      <span className="bw-toast-text">{undoMessage(state)}</span>
      {(state.phase === "offered" || state.phase === "undoing") && (
        <button type="button" className="bw-btn bw-toast-action" onClick={onUndo} disabled={state.phase === "undoing"}>Undo</button>
      )}
      {state.phase !== "undoing" && <DismissButton onDismiss={onDismiss} />}
      {state.phase === "offered" && <ToastTimer key={state.actionId} ms={offerMs} />}
    </div>
  );
}
